import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TopBar from '../../components/TopBar'
import { useAnalytics } from '../../context/AnalyticsContext'
import { getOnboarding, setOnboarding } from './onboardingStorage'
import { searchCities } from '../../lib/cityAutocomplete'
import './onboarding.css'

function yearsList() {
  const current = new Date().getFullYear()
  const years = []
  for (let y = current; y >= 1960; y--) years.push(y)
  return years
}

function isAdult(year, month, day) {
  const now = new Date()
  const birth = new Date(year, month - 1, day)
  let age = now.getFullYear() - birth.getFullYear()
  const m = now.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--
  return age >= 18
}

const OnboardingBirth = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const saved = useMemo(() => getOnboarding(), [])

  const [day, setDay] = useState(saved.birthDay || '')
  const [month, setMonth] = useState(saved.birthMonth || '')
  const [year, setYear] = useState(saved.birthYear || '')
  const [time, setTime] = useState(saved.birthTime || '')
  const [place, setPlace] = useState(saved.birthPlace || '')
  const [placeSuggestions, setPlaceSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [error, setError] = useState('')
  const [searching, setSearching] = useState(false)

  const onBack = () => navigate('/onboarding/name')

  const onContinue = () => {
    if (!day || !month || !year) {
      setError(t('onboarding.birthDateRequired'))
      return
    }
    if (!isAdult(Number(year), Number(month), Number(day))) {
      setError(t('onboarding.birthAdultRequired'))
      return
    }
    setError('')
    setOnboarding({
      birthDay: Number(day),
      birthMonth: Number(month),
      birthYear: Number(year),
      birthTime: time || null,
      birthPlace: place.trim() || null
    })
    trackEvent('funnel_birth_completed', { step: 3, hasTime: !!time, hasPlace: !!place })
    navigate('/onboarding/interests')
  }

  const days = Array.from({ length: 31 }).map((_, i) => i + 1)
  const months = [
    { v: 1, label: 'Jan' },
    { v: 2, label: 'Feb' },
    { v: 3, label: 'Mar' },
    { v: 4, label: 'Apr' },
    { v: 5, label: 'May' },
    { v: 6, label: 'Jun' },
    { v: 7, label: 'Jul' },
    { v: 8, label: 'Aug' },
    { v: 9, label: 'Sep' },
    { v: 10, label: 'Oct' },
    { v: 11, label: 'Nov' },
    { v: 12, label: 'Dec' }
  ]
  const years = yearsList()

  return (
    <div className="onboarding-page">
      <TopBar currentStep={3} totalSteps={10} onBack={onBack} showBack={true} />
      <div className="onboarding-content">
        <h1 className="h1">{t('onboarding.birthTitle')}</h1>
        <p className="p">
          {t('onboarding.birthSubtitle')}
        </p>

        <div className="field">
          <div className="label">{t('onboarding.birthDateLabel')}</div>
          <div className="row">
            <select className="select" value={day} onChange={(e) => setDay(e.target.value)}>
              <option value="">{t('onboarding.birthDay')}</option>
              {days.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select className="select" value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">{t('onboarding.birthMonth')}</option>
              {months.map((m) => (
                <option key={m.v} value={m.v}>
                  {m.label}
                </option>
              ))}
            </select>
            <select className="select" value={year} onChange={(e) => setYear(e.target.value)}>
              <option value="">{t('onboarding.birthYear')}</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field field-time">
          <label className="label">{t('onboarding.birthTimeLabel')}</label>
          <input className="input input-time-full" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>

        <div className="field" style={{ position: 'relative' }}>
          <label className="label">{t('onboarding.birthPlaceLabel')}</label>
          <input
            className="input"
            value={place}
            onChange={async (e) => {
              const value = e.target.value
              setPlace(value)
              if (value.length >= 2) {
                setSearching(true)
                const cities = await searchCities(value)
                setPlaceSuggestions(cities)
                setShowSuggestions(true)
                setSearching(false)
              } else {
                setPlaceSuggestions([])
                setShowSuggestions(false)
              }
            }}
            onFocus={() => {
              if (placeSuggestions.length > 0) setShowSuggestions(true)
            }}
            onBlur={() => {
              // Задержка чтобы клик по предложению успел сработать
              setTimeout(() => setShowSuggestions(false), 200)
            }}
            placeholder={t('onboarding.birthPlacePlaceholder')}
          />
          {showSuggestions && placeSuggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #eee',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000,
              marginTop: '4px'
            }}>
              {placeSuggestions.map((city, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setPlace(city.fullName)
                    setShowSuggestions(false)
                  }}
                  style={{
                    padding: '12px',
                    cursor: 'pointer',
                    borderBottom: idx < placeSuggestions.length - 1 ? '1px solid #eee' : 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  {city.fullName}
                </div>
              ))}
            </div>
          )}
          {searching && (
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>{t('common.searching')}</div>
          )}
        </div>

        {error ? <div className="error-text">{error}</div> : null}
      </div>

      <button className="cta-fixed" onClick={onContinue}>
        {t('common.continue')}
      </button>
    </div>
  )
}

export default OnboardingBirth

